import { useSuspenseQueries } from '@tanstack/react-query';
import { Suspense, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
    OPEN_APISPEC_SLUG_MAX_LENGTH,
    OPEN_APISPEC_SLUG_MIN_LENGTH,
    OPEN_APISPEC_SLUG_PATTERN,
    type OpenAPISpec,
} from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Label,
    LoadingPanel,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';
import { normalizeURLSlug } from '@gitbook/util-url';

import { apiClient, apiQuery, apiQueryClient } from '../../state';
import {
    type OpenAPISourceInputs,
    type OpenAPISourceType,
    OpenAPISpecSourceFields,
} from './OpenAPISpecSourceFields';
import { OpenAPIWaitForSpec } from './OpenAPIWaitForSpec';

interface OpenAPISpecCreateDialogProps {
    organizationId: string;
    onCreated: (openAPISpec: OpenAPISpec) => void;
    analyticsContext: analytics.OpenAPIAnalyticsCreateContext;
}

export function OpenAPISpecCreateDialog(props: OpenAPISpecCreateDialogProps) {
    const { organizationId, onCreated, analyticsContext } = props;
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Add OpenAPI specification</Heading>
                <Description>
                    Upload a file or link a URL to automatically generate API documentation.
                </Description>
            </DialogHeader>
            <Suspense fallback={<LoadingPanel />}>
                <OpenAPICreateForm
                    organizationId={organizationId}
                    onCreated={onCreated}
                    analyticsContext={analyticsContext}
                />
            </Suspense>
        </Dialog>
    );
}

interface OpenAPICreateFormInputs extends OpenAPISourceInputs {
    /**
     * Slug of the OpenAPI spec.
     */
    slug: string;
    /**
     * Type of spec source.
     */
    type: OpenAPISourceType | null;
}

function OpenAPICreateForm(props: OpenAPISpecCreateDialogProps) {
    const { organizationId, onCreated, analyticsContext } = props;
    const [step, setStep] = useState<'source' | 'process'>('source');
    const [createdSpec, setCreatedSpec] = useState<OpenAPISpec | null>(null);
    const [{ data: organization }, { data: specs }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('listOpenAPISpecs', { organizationId, limit: 100 }),
        ],
    });
    const doneRef = useRef<null | ((args: { close: boolean }) => void)>(null);
    const hasSlugSuggestion = specs.items.length === 0;
    const form = useForm<OpenAPICreateFormInputs>({
        defaultValues: {
            url: '',
            text: null,
            type: 'url',
            slug: hasSlugSuggestion ? normalizeURLSlug(`${organization.title} API`) : '',
        },
    });
    const type = form.watch('type');

    const [isPending, startDialogAction] = useDialogAction();
    const onSubmit: SubmitHandler<OpenAPICreateFormInputs> = async (data) => {
        const { type: sourceType } = data;
        switch (step) {
            case 'source': {
                assert(sourceType, 'type must be set');
                assert(sourceType !== 'cli', 'CLI source should not be used to submit');
                const source: { url: string } | { text: string } = (() => {
                    switch (sourceType) {
                        case 'url': {
                            assert(data.url, 'url must be set');
                            return { url: data.url };
                        }
                        case 'file': {
                            assert(data.text, 'text must be set');
                            return { text: data.text.content };
                        }
                        default:
                            assertNever(sourceType);
                    }
                })();
                await startDialogAction(async ({ close }) => {
                    await toast.catch(async () => {
                        const response = await apiClient.openapi.createOpenApiSpec(organizationId, {
                            source,
                            slug: data.slug,
                        });
                        analytics.logEvent({
                            type: 'openapi_spec_create',
                            organization: organizationId,
                            sourceType,
                            ...analyticsContext,
                        });
                        const createdSpec = response.data;
                        // Prefetch the spec to avoid ending up on a screen with loading spinner.
                        await apiQueryClient.prefetchQuery(
                            apiQuery('getOpenAPISpecBySlug', {
                                organizationId,
                                specSlug: createdSpec.slug,
                            })
                        );
                        setCreatedSpec(createdSpec);
                        setStep('process');
                        await new Promise<void>((resolve) => {
                            doneRef.current = (args) => {
                                if (args.close) {
                                    close().then(resolve);
                                } else {
                                    resolve();
                                }
                            };
                        });
                    }, 'Error while creating OpenAPI specification');
                });
                return;
            }
            case 'process': {
                return;
            }
            default: {
                assertNever(step);
            }
        }
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                {(() => {
                    switch (step) {
                        case 'source': {
                            assert(type, 'type must be set');
                            return (
                                <>
                                    <TextField
                                        name="slug"
                                        minLength={OPEN_APISPEC_SLUG_MIN_LENGTH}
                                        maxLength={OPEN_APISPEC_SLUG_MAX_LENGTH}
                                        pattern={{
                                            value: new RegExp(OPEN_APISPEC_SLUG_PATTERN),
                                            message:
                                                'Invalid name, only alphanumeric characters and dash are allowed.',
                                        }}
                                        required
                                        className="mb-4"
                                    >
                                        <Label>Name</Label>
                                        <Description>
                                            The name is used to reference the specification in
                                            GitSync or CLI. Cannot be changed.
                                        </Description>
                                        <TextInput
                                            autoFocus={!hasSlugSuggestion}
                                            placeholder="my-api"
                                            disabled={isPending}
                                        />
                                        <FieldError />
                                    </TextField>
                                    <OpenAPISpecSourceFields
                                        onTypeChange={(type) => {
                                            form.setValue('type', type);
                                        }}
                                        type={type}
                                        control={form.control}
                                        disabled={isPending}
                                        autoFocus={hasSlugSuggestion}
                                        specSlug={form.watch('slug')}
                                        organizationId={organizationId}
                                        onDone={onCreated}
                                    />
                                </>
                            );
                        }
                        case 'process': {
                            assert(createdSpec);
                            // Even if prefetched, better to use suspense to avoid flickering
                            return (
                                <Suspense fallback={<LoadingPanel />}>
                                    <OpenAPIWaitForSpec
                                        organizationId={organizationId}
                                        specSlug={createdSpec.slug}
                                        errorAction={
                                            <Button
                                                label="Edit specification"
                                                onPress={() => {
                                                    setStep('source');
                                                }}
                                            />
                                        }
                                        onError={() => {
                                            assert(doneRef.current);
                                            doneRef.current({ close: false });
                                        }}
                                        onReady={() => {
                                            onCreated(createdSpec);
                                            assert(doneRef.current);
                                            doneRef.current({ close: true });
                                        }}
                                    />
                                </Suspense>
                            );
                        }
                        default:
                            assertNever(step);
                    }
                })()}
            </DialogBody>
            {step === 'process' ? null : (
                <DialogFooter>
                    <DialogCloseButton />
                    {type !== 'cli' && <Submit label={step === 'source' ? 'Add' : 'Continue'} />}
                </DialogFooter>
            )}
        </Form>
    );
}
